/** The 2.0 premium kit: React pieces that draw exactly what the design canvas draws (premium.css, classes p-). */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { Icon, initials, type IconName } from './ui';

type Tone = '' | 's' | 'g' | 'r' | 'k';
const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

export function Screen({ children, gap, className }: { children: ReactNode; gap?: number; className?: string }) {
  return <main className={cx('p-scr', className)} style={gap ? { gap } : undefined}>{children}</main>;
}

export const Sp = () => <span className="p-sp" />;

export const Top = ({ children }: { children: ReactNode }) => <div className="p-top">{children}</div>;

export function Back({ to, label, onClick }: { to?: string; label: string; onClick?: () => void }) {
  const inner = <><Icon name="back" size={18} sw={2} />{label}</>;
  return to ? <Link className="p-back" to={to}>{inner}</Link> : <button type="button" className="p-back" onClick={onClick}>{inner}</button>;
}

export function IB({ icon, label, to, onClick }: { icon: IconName | 'dots'; label: string; to?: string; onClick?: () => void }) {
  const inner = icon === 'dots'
    ? <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
    : <Icon name={icon} size={17} sw={1.9} />;
  return to
    ? <Link className="p-ib" to={to} aria-label={label}>{inner}</Link>
    : <button type="button" className="p-ib" aria-label={label} onClick={onClick}>{inner}</button>;
}

export const Kick = ({ children, s, style }: { children: ReactNode; s?: boolean; style?: CSSProperties }) => (
  <span className={cx('p-kick', s && 's')} style={style}>{children}</span>
);

export function Title({ h, sub, badge, kick, size }: { h: ReactNode; sub?: ReactNode; badge?: ReactNode; kick?: ReactNode; size?: number }) {
  const head = <h1 className="p-h1" style={size ? { fontSize: size } : undefined}>{h}</h1>;
  return (
    <div className="p-stack">
      {kick && <Kick>{kick}</Kick>}
      {badge ? <div className="p-tline">{head}{badge}</div> : head}
      {sub && <span className="p-sub">{sub}</span>}
    </div>
  );
}

export function Hero({ children, to, style, label }: { children: ReactNode; to?: string; style?: CSSProperties; label?: string }) {
  return to
    ? <Link className="p-hero" to={to} style={style} aria-label={label}>{children}</Link>
    : <section className="p-hero" style={style} aria-label={label}>{children}</section>;
}

export function Card({ children, pad, style, className }: { children: ReactNode; pad?: boolean; style?: CSSProperties; className?: string }) {
  return <div className={cx('p-card', pad && 'p-pad', className)} style={style}>{children}</div>;
}

export function Row({ t, s, left, right, to, href, onClick, chev, style, label, disabled }: {
  t: ReactNode; s?: ReactNode; left?: ReactNode; right?: ReactNode; to?: string; href?: string; onClick?: () => void;
  chev?: boolean; style?: CSSProperties; label?: string; disabled?: boolean;
}) {
  const inner = (
    <>
      {left}
      <span className="p-t"><b>{t}</b>{s && <small>{s}</small>}</span>
      {right}
      {chev && <Icon name="right" size={16} sw={2} style={{ flex: 'none', color: '#8C857B' }} />}
    </>
  );
  if (to) return <Link className="p-row" to={to} style={style} aria-label={label}>{inner}</Link>;
  if (href) return <a className="p-row" href={href} target="_blank" rel="noreferrer" style={style} aria-label={label}>{inner}</a>;
  if (onClick) return <button type="button" className="p-row" onClick={onClick} style={style} aria-label={label} disabled={disabled}>{inner}</button>;
  return <div className="p-row" style={style}>{inner}</div>;
}

export function Ic({ icon, tone = '', text, size = 17 }: { icon?: IconName; tone?: Tone; text?: string; size?: number }) {
  return <span className={cx('p-ic', tone)} aria-hidden="true">{icon ? <Icon name={icon} size={size} sw={1.9} /> : text}</span>;
}

export const Av = ({ name, dark }: { name: string; dark?: boolean }) => <span className={cx('p-av', dark && 'k')} aria-hidden="true">{initials(name)}</span>;

export const Badge = ({ tone = '', children }: { tone?: Tone; children: ReactNode }) => <span className={cx('p-badge', tone)}>{children}</span>;

export const Amt = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => <span className="p-amt" style={style}>{children}</span>;

/** An amount over a badge, right of a row. */
export const Ends = ({ amt, badge }: { amt: ReactNode; badge?: ReactNode }) => <span className="p-ends"><span className="p-amt">{amt}</span>{badge}</span>;

type BtnProps = { label: ReactNode; to?: string; href?: string; onClick?: () => void; disabled?: boolean; busy?: boolean; style?: CSSProperties; type?: 'button' | 'submit' };

/** The one main button: ink, with a saffron square at the end. */
export function Btn({ label, to, href, onClick, disabled, busy, style, type = 'button', go = 'arrow', center }: BtnProps & { go?: IconName | 'rupee' | null; center?: boolean }) {
  const goEl = go === null || center ? null : <span className="p-go" aria-hidden="true">{go === 'rupee' ? '₹' : <Icon name={go} size={18} sw={2.2} />}</span>;
  const cls = cx('p-btn', (center || go === null) && 'c');
  const inner = <>{busy ? 'Saving…' : label}{goEl}</>;
  if (to && !disabled) return <Link className={cls} to={to} style={style}>{inner}</Link>;
  if (href && !disabled) return <a className={cls} href={href} target="_blank" rel="noreferrer" style={style} onClick={onClick}>{inner}</a>;
  return <button type={type} className={cls} onClick={onClick} disabled={disabled || busy} style={style}>{inner}</button>;
}

export function Btn2({ label, to, href, onClick, disabled, icon, red, style }: BtnProps & { icon?: IconName; red?: boolean }) {
  const cls = cx('p-btn2', red && 'r');
  const inner = <>{icon && <Icon name={icon} size={17} sw={1.9} />}{label}</>;
  if (to && !disabled) return <Link className={cls} to={to} style={style}>{inner}</Link>;
  if (href && !disabled) return <a className={cls} href={href} target="_blank" rel="noreferrer" style={style} onClick={onClick}>{inner}</a>;
  return <button type="button" className={cls} onClick={onClick} disabled={disabled} style={style}>{inner}</button>;
}

export function Sm({ label, to, href, onClick, k, y, disabled, ariaLabel }: { label: ReactNode; to?: string; href?: string; onClick?: () => void; k?: boolean; y?: boolean; disabled?: boolean; ariaLabel?: string }) {
  const cls = cx('p-sm', k && 'k', y && 'y');
  if (to) return <Link className={cls} to={to} aria-label={ariaLabel}>{label}</Link>;
  if (href) return <a className={cls} href={href} target="_blank" rel="noreferrer" aria-label={ariaLabel}>{label}</a>;
  return <button type="button" className={cls} onClick={onClick} disabled={disabled} aria-label={ariaLabel}>{label}</button>;
}

export const Pair = ({ children }: { children: ReactNode }) => <span className="p-pair">{children}</span>;

export function Seg<T extends string>({ options, value, onChange, label }: { options: Array<{ value: T; label: ReactNode }>; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="p-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className={o.value === value ? 'on' : undefined} aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export const Chips = ({ children, label }: { children: ReactNode; label?: string }) => <div className="p-chips" role={label ? 'group' : undefined} aria-label={label}>{children}</div>;

export function Chip({ children, on, onClick, to }: { children: ReactNode; on?: boolean; onClick?: () => void; to?: string }) {
  if (to) return <Link className={cx('p-chip', on && 'on')} to={to} aria-current={on ? 'true' : undefined}>{children}</Link>;
  return <button type="button" className={cx('p-chip', on && 'on')} aria-pressed={on} onClick={onClick}>{children}</button>;
}

export function Field({ label, id, value, onChange, placeholder, type = 'text', inputMode, error, readOnly, autoComplete }: {
  label: string; id: string; value: string; onChange?: (v: string) => void; placeholder?: string; type?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'decimal'; error?: string; readOnly?: boolean; autoComplete?: string;
}) {
  return (
    <div className="p-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} value={value} placeholder={placeholder} inputMode={inputMode} readOnly={readOnly} autoComplete={autoComplete}
        onChange={(e) => onChange?.(e.target.value)} aria-invalid={error ? true : undefined} aria-describedby={error ? id + '-error' : undefined} />
      {error && <small id={id + '-error'} className="p-err" role="alert">{error}</small>}
    </div>
  );
}

export const G2 = ({ children }: { children: ReactNode }) => <div className="p-g2">{children}</div>;

export function Note({ children, icon = 'info', to }: { children: ReactNode; icon?: IconName; to?: string }) {
  const inner = <><Icon name={icon} size={18} sw={1.9} color="#8A5A00" /><span>{children}</span></>;
  return to ? <Link className="p-note" to={to}>{inner}</Link> : <div className="p-note">{inner}</div>;
}

export function Bar({ frac, color }: { frac: number; color?: string }) {
  return <div className="p-bar"><i style={{ width: `${Math.round(Math.max(0, Math.min(1, frac)) * 100)}%`, background: color }} /></div>;
}

export function Group({ label, right, children, gap = 10 }: { label: ReactNode; right?: ReactNode; children: ReactNode; gap?: number }) {
  return (
    <div className="p-stack" style={{ gap }}>
      {right ? <div className="p-sec"><Kick>{label}</Kick>{right}</div> : <Kick>{label}</Kick>}
      {children}
    </div>
  );
}

/** A bottom sheet over a dimmed screen: grab bar, one job, one main button. */
export function Sheet({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
    )).filter((element) => element.getClientRects().length > 0);
    (focusable()[0] ?? dialog).focus();
    const onKey = (event: KeyboardEvent) => {
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs[dialogs.length - 1] !== dialog) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== 'Tab') return;
      const controls = focusable();
      const first = controls[0] ?? dialog;
      const last = controls[controls.length - 1] ?? dialog;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  return createPortal(
    <div className="p-dim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section ref={dialogRef} tabIndex={-1} className="p-sheet p-scr" role="dialog" aria-modal="true" aria-label={label}>
        <span className="p-grab" aria-hidden="true" />
        {children}
      </section>
    </div>,
    document.body,
  );
}

export function SheetHead({ kick, title, onClose }: { kick?: ReactNode; title: ReactNode; onClose: () => void }) {
  return (
    <Top>
      <div className="p-stack">{kick && <Kick>{kick}</Kick>}<h1 className="p-h1" style={{ fontSize: 26 }}>{title}</h1></div>
      <Sp />
      <IB icon="close" label="Close" onClick={onClose} />
    </Top>
  );
}

export function Tile({ to, icon, tone = '', t, s }: { to: string; icon: IconName; tone?: Tone; t: ReactNode; s?: ReactNode }) {
  return (
    <Link className="p-tile" to={to}>
      <Ic icon={icon} tone={tone} size={16} />
      <span className="p-stack" style={{ gap: 2, minWidth: 0 }}><b>{t}</b>{s && <small>{s}</small>}</span>
    </Link>
  );
}

/** A choice drawn as a card (a plan): the picked one has an ink outline. */
export function Opt({ t, s, right, on, onClick }: { t: ReactNode; s?: ReactNode; right?: ReactNode; on: boolean; onClick: () => void }) {
  return (
    <button type="button" className={cx('p-opt', on && 'on')} aria-pressed={on} onClick={onClick}>
      <span className="p-t"><b>{t}</b>{s && <small>{s}</small>}</span>
      {right}
    </button>
  );
}

export const KV = ({ k, v }: { k: ReactNode; v: ReactNode }) => <div className="p-kv"><span>{k}</span><b>{v}</b></div>;

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="p-empty"><b>{title}</b>{children && <span>{children}</span>}</div>;
}

export const Foot = () => <span className="p-foot">Powered by <b>tiffinify</b></span>;

export const Mk = ({ food }: { food: 'veg' | 'nonveg' | 'egg' }) => (
  <span className={cx('p-mk', food === 'nonveg' && 'n', food === 'egg' && 'e')} role="img" aria-label={food === 'veg' ? 'Veg' : food === 'egg' ? 'Egg' : 'Non-veg'} />
);
